import { monthRange, fetchPaged } from './contabilitaAPI.js';

// -----------------------------------------------------------------------------
//  REPORT MENSILE DEL PARTNER — un file Excel, una riga per CORSA.
//
//  Ci sono le tratte degli spostamenti interni e le gambe delle prenotazioni
//  (pickup, transfer, notturno) del mese italiano, in ordine di partenza; la
//  colonna «Servizio» dice quale, la colonna «Stato» a che punto è.
//
//  Ci sono ANCHE le tratte eliminate e le prenotazioni annullate, con il loro
//  stato: una corsa eliminata ma contabilizzata il partner la paga comunque
//  (come in COD3). Su queste righe km e minuti restano vuoti — la corsa non è
//  stata fatta — così i totali in fondo contano solo il lavoro svolto.
//
//  KM e MINUTI sono quelli che il COD6 paga a driver e jolly:
//   - spostamenti interni: km della tratta; minuti dalla partenza all'arrivo
//     più i minuti aggiuntivi decisi all'assegnazione (`extra_min_driver` /
//     `extra_min_jolly`). Il jolly c'è sulle tratte a 2 operatori, tranne
//     quando Simba fa rientrare il driver con i mezzi pubblici (colonna
//     «Accompagnato»);
//   - prenotazioni: km della gamba (`simba_km`); minuti dal planner del driver
//     e da quello del jolly, che c'è solo sulle gambe a 2 operatori.
//
//  Partenza e arrivo delle gambe seguono la lista Booking: il pickup va dal
//  cliente alla sede all'andata e al contrario al ritorno, il transfer usa i
//  suoi due punti.
//
//  Nessun endpoint dedicato: si leggono `/journey` e `/booking` come fanno le
//  pagine di contabilità, e se il backend tronca le righe il report non si
//  genera (un report con corse mancanti sembrerebbe completo).
// -----------------------------------------------------------------------------

const TZ = 'Europe/Rome';
const fmtGiorno = new Intl.DateTimeFormat('it-IT', { timeZone: TZ, weekday: 'long' });
const fmtData = new Intl.DateTimeFormat('it-IT', {
  timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric',
});
const fmtOra = new Intl.DateTimeFormat('it-IT', {
  timeZone: TZ, hour: '2-digit', minute: '2-digit',
});

const valido = (d) => d && !Number.isNaN(new Date(d).getTime());
const minutiTra = (a, b) => {
  if (!valido(a) || !valido(b)) return 0;
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return ms > 0 ? Math.round(ms / 60000) : 0;
};
const km2 = (v) => (v === null || v === undefined || v === '' ? null : Math.round(Number(v) * 100) / 100);

// Via e civico, senza città: la città ha una colonna sua.
const via = (p) => (p ? [p.address, p.street_number].filter(Boolean).join(' ').trim() : '');
const citta = (p) => (p?.city || '').trim();

// Stato di una tratta di spostamento: lo stesso campo unico del dettaglio
// nel gestionale (pianificazione e avanzamento letti insieme).
const STATO_TRATTA = {
  PROGRAMMATO: 'Programmata',
  MODIFICATO: 'Modificata',
  CONFERMATA: 'Confermata',
  IN_PARTENZA: 'Driver in partenza',
  IN_VIAGGIO: 'Driver in viaggio',
  ARRIVATO: 'Driver arrivato',
  CONSEGNATO: 'Veicolo consegnato',
  CONCLUSA: 'Conclusa',
};
const statoTratta = (r) => {
  if (r.status === 'ELIMINATA') return r.is_billed ? 'Eliminata · contabilizzata' : 'Eliminata';
  if (r.status === 'CONCLUSA') return STATO_TRATTA.CONCLUSA;
  return STATO_TRATTA[r.execution_status || r.status || 'PROGRAMMATO'] || r.status;
};

// Stato di una prenotazione: le stesse parole della lista Booking. Vale per
// tutte le sue gambe.
const statoPrenotazione = (b) => {
  if (b.state === 'end') return 'Concluso';
  if (b.state === 'canceled') return 'Annullato';
  if (b.state === 'before') return 'Non ancora confermata';
  if (b.state === 'accepted') return 'Accettato';
  return 'In corso';
};

const CAMBIO = { MANUAL: 'Manuale', AUTOMATIC: 'Automatico', manual: 'Manuale', automatic: 'Automatico' };

// Il servizio con le parole del gestionale (vedi ServiceChip). Sugli
// spostamenti c'è anche il tipo: singolo (2 operatori) o a catena (1).
const SERVIZI = {
  pickup: 'Pickup & Delivery',
  pickup_loaner: 'Pickup · auto cortesia',
  escort: 'Transfer',
  escort_loaner: 'Transfer · auto cortesia',
};
const servizioPrenotazione = (b) => {
  // A UI il notturno è un `type_booking`, non un `type_prenotazione`; ma
  // `type_booking` segue il partner, e un Transfer resta un Transfer.
  const tipo = b.type_prenotazione || 'pickup';
  if (b.type_booking === 'restaurant' && !tipo.startsWith('escort')) return 'Notturno';
  const conCortesia = b.loaner && !tipo.endsWith('_loaner') ? `${tipo}_loaner` : tipo;
  return SERVIZI[conCortesia] || SERVIZI[tipo] || 'Prenotazione';
};

// --- spostamenti interni: una riga per tratta ---------------------------------
function righeSpostamento(journey) {
  return (journey.routes || [])
    .map((r) => {
      const dueOperatori = r.two_operators === true;
      // Singolo senza jolly: Simba ha deciso che il driver rientra con i mezzi
      // pubblici. Resta singolo (N. op. 2, come l'ha chiesto il partner) ma
      // il jolly non c'è: niente km né minuti del jolly.
      const mezzi = dueOperatori && !!r.rientro_altrove;
      const conJolly = dueOperatori && !mezzi;
      // Una tratta eliminata non è stata fatta: niente km né minuti.
      const fatta = r.status !== 'ELIMINATA';
      // Senza orari (dato vecchio) vale la stima salvata con la tratta.
      const durata = minutiTra(r.dateStart, r.dateEnd) || Math.round(Number(r.min) || 0);
      return {
        start: r.dateStart || journey.date,
        servizio: dueOperatori ? 'Spostamento singolo' : 'Spostamento a catena',
        stato: statoTratta(r),
        targa: r.targa || '',
        marca: r.marca || '',
        modello: r.modello || '',
        cambio: CAMBIO[r.gearbox_type] || '',
        operatori: dueOperatori ? 2 : 1,
        accompagnato: conJolly ? 'Sì (jolly)' : mezzi ? 'No (mezzi pubblici)' : 'No',
        cittaPartenza: citta(r.startPoint),
        partenza: via(r.startPoint),
        cittaArrivo: citta(r.endPoint),
        arrivo: via(r.endPoint),
        kmDriver: fatta ? km2(r.km) : null,
        minDriver: fatta ? durata + (Number(r.extra_min_driver) || 0) : null,
        kmJolly: fatta && conJolly ? km2(r.km) : null,
        minJolly: fatta && conJolly ? durata + (Number(r.extra_min_jolly) || 0) : null,
      };
    });
}

// --- prenotazioni: una riga per gamba -----------------------------------------
function righePrenotazione(booking) {
  const car = booking.car || {};
  const sede = booking.headquarter || null;
  const veicolo = {
    targa: car.targa || '',
    marca: car.marca || '',
    modello: car.modello || '',
    // `both` = cambio non indicato: meglio vuoto che inventato.
    cambio: CAMBIO[car.transmission] || '',
  };

  // Una prenotazione annullata non è stata fatta: niente km né minuti.
  const fatta = booking.state !== 'canceled';
  const stato = statoPrenotazione(booking);

  const riga = (leg, goes, da, a) => {
    const planner = (tipo) => (leg.planners || []).find((p) => p.type === tipo) || null;
    const pDriver = planner('driver');
    const pJolly = planner('jolly');
    // Orario della gamba come nella lista Booking: partenza = `date_customer`
    // all'andata, `date_partner` al ritorno (con ripiego sull'altra).
    const start = goes
      ? leg.date_customer || leg.date_partner
      : leg.date_partner || leg.date_customer;
    return {
      start: start || pDriver?.date_start || booking.data,
      servizio: servizioPrenotazione(booking),
      stato,
      ...veicolo,
      operatori: pJolly ? 2 : 1,
      accompagnato: pJolly ? 'Sì (jolly)' : 'No',
      cittaPartenza: citta(da),
      partenza: via(da),
      cittaArrivo: citta(a),
      arrivo: via(a),
      kmDriver: fatta ? km2(leg.simba_km) : null,
      minDriver: fatta && pDriver ? minutiTra(pDriver.date_start, pDriver.date_end) : null,
      kmJolly: fatta && pJolly ? km2(leg.simba_km) : null,
      minJolly: fatta && pJolly ? minutiTra(pJolly.date_start, pJolly.date_end) : null,
    };
  };

  return [
    ...(booking.pickup_bookings || []).map((leg) => {
      const goes = leg.type_booking !== 'return';
      return goes ? riga(leg, goes, leg.point, sede) : riga(leg, goes, sede, leg.point);
    }),
    ...(booking.escort_bookings || []).map((leg) =>
      riga(leg, leg.type_booking !== 'return', leg.point_origin_escort, leg.point_destination_escort)),
  ];
}

// Le righe del mese, già ordinate. `{ truncated: true }` se il backend ha
// tagliato una delle due letture: il chiamante non deve generare il file.
export async function loadPartnerMonthRows(partnerId, year, month) {
  const { from, to } = monthRange(year, month);
  const [jr, bk] = await Promise.all([
    fetchPaged('/journey', {
      join: ['routes', 'routes.startPoint', 'routes.endPoint'],
      s: JSON.stringify({
        $and: [{ partnerId: { $eq: partnerId } }, { date: { $gte: from, $lt: to } }],
      }),
    }),
    fetchPaged('/booking', {
      join: [
        'car',
        'headquarter',
        'pickup_bookings',
        'pickup_bookings.point',
        'pickup_bookings.planners',
        'escort_bookings',
        'escort_bookings.point_origin_escort',
        'escort_bookings.point_destination_escort',
        'escort_bookings.planners',
      ],
      s: JSON.stringify({
        // Anche le annullate: il report le mostra con il loro stato.
        $and: [
          { partnerId: { $eq: partnerId } },
          { data: { $gte: from, $lt: to } },
        ],
      }),
    }),
  ]);
  if (jr.truncated || bk.truncated) return { rows: [], truncated: true };

  const rows = [
    ...jr.rows.flatMap(righeSpostamento),
    ...bk.rows.flatMap(righePrenotazione),
  ].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  return { rows, truncated: false };
}

const COLONNE = [
  { header: 'Giorno', key: 'giorno', width: 12 },
  { header: 'Data', key: 'data', width: 12 },
  { header: 'Orario', key: 'orario', width: 9 },
  { header: 'Servizio', key: 'servizio', width: 22 },
  { header: 'Stato', key: 'stato', width: 24 },
  { header: 'Targa', key: 'targa', width: 12 },
  { header: 'Marca', key: 'marca', width: 14 },
  { header: 'Modello', key: 'modello', width: 16 },
  { header: 'Cambio', key: 'cambio', width: 12 },
  { header: 'N. op.', key: 'operatori', width: 8 },
  // Il driver ha il jolly che lo riporta? Sullo spostamento singolo Simba
  // può decidere che rientri con i mezzi pubblici.
  { header: 'Accompagnato', key: 'accompagnato', width: 20 },
  { header: 'Città partenza', key: 'cittaPartenza', width: 18 },
  { header: 'Partenza', key: 'partenza', width: 30 },
  { header: 'Città arrivo', key: 'cittaArrivo', width: 18 },
  { header: 'Destinazione', key: 'arrivo', width: 30 },
  { header: 'KM Driver', key: 'kmDriver', width: 11 },
  { header: 'MIN Driver', key: 'minDriver', width: 11 },
  { header: 'KM Jolly', key: 'kmJolly', width: 11 },
  { header: 'MIN Jolly', key: 'minJolly', width: 11 },
];

const MESI = [
  'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre',
];

// Il file Excel, come Blob. ExcelJS si carica solo qui: è pesante e serve
// solo a chi scarica il report.
export async function buildPartnerReportXlsx(rows, { partnerName = '', year, month }) {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Simba';
  const ws = wb.addWorksheet(`${MESI[month - 1]} ${year}`, {
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  ws.columns = COLONNE;

  for (const r of rows) {
    const d = new Date(r.start);
    const ok = valido(r.start);
    ws.addRow({
      ...r,
      giorno: ok ? fmtGiorno.format(d) : '',
      data: ok ? fmtData.format(d) : '',
      orario: ok ? fmtOra.format(d) : '',
    });
  }

  // Totali in fondo: è un riepilogo mensile, le somme servono.
  if (rows.length) {
    const ultima = rows.length + 1;
    const tot = ws.addRow({ giorno: 'Totale' });
    for (const key of ['kmDriver', 'minDriver', 'kmJolly', 'minJolly']) {
      const col = ws.getColumn(key).letter;
      tot.getCell(key).value = { formula: `SUM(${col}2:${col}${ultima})` };
    }
    tot.font = { bold: true };
  }

  const head = ws.getRow(1);
  head.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  head.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF232C51' } };
  head.alignment = { vertical: 'middle' };
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: COLONNE.length } };
  for (const key of ['kmDriver', 'kmJolly']) ws.getColumn(key).numFmt = '0.00';

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const nome = (partnerName || 'partner').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '');
  return { blob, filename: `report-${nome}-${year}-${String(month).padStart(2, '0')}.xlsx` };
}
